import type { Contract, PriceBand } from './api/generated/types.gen';

export type Horizon = 'hourly' | '15m';
/** Hourly has two contract families on the same close; 'range' is set only for range buckets. */
export type Place = { coin: string; horizon: Horizon; family?: 'range' };
export const HORIZON_NAMES: Record<Horizon, string> = { hourly: '1 hour', '15m': '15 min' };

/** The desk route: /desk/{COIN}/{hourly|15m}, with ?family=range for hourly buckets. Anything else lands on BTC hourly. */
export function placeFromPath(path: string, search = ''): Place {
  const m = /^\/desk\/([A-Za-z]+)\/(hourly|15m)\/?$/.exec(path);
  if (!m) return { coin: 'BTC', horizon: 'hourly' };
  const place: Place = { coin: m[1].toUpperCase(), horizon: m[2] as Horizon };
  return place.horizon === 'hourly' && new URLSearchParams(search).get('family') === 'range' ? { ...place, family: 'range' } : place;
}
export const pathOf = (p: Place) => `/desk/${p.coin}/${p.horizon}`;
export const familyOf = (p: Place) => p.family ?? (p.horizon === '15m' ? 'up_down' : 'above_below');
export const samePlace = (a: Place, b: Place) => a.coin === b.coin && a.horizon === b.horizon && a.family === b.family;

// Range series names are not derivable from the coin (SOL's is KXSOLE), so they are listed as the registry lists them.
const RANGE_SERIES: Record<string, string> = { KXBTC: 'BTC', KXETH: 'ETH', KXSOLE: 'SOL', KXXRP: 'XRP', KXDOGE: 'DOGE', KXBNB: 'BNB', KXHYPE: 'HYPE' };

/** Kalshi series are KX{COIN}D (hourly), KX{COIN}15M (15-minute) and the hourly range series above. */
export function placeOfTicker(ticker: string): Place | null {
  const series = ticker.split('-')[0];
  if (RANGE_SERIES[series]) return { coin: RANGE_SERIES[series], horizon: 'hourly', family: 'range' };
  const m = /^KX([A-Z]+?)(15M|D)$/.exec(series);
  return m ? { coin: m[1], horizon: m[2] === 'D' ? 'hourly' : '15m' } : null;
}

/** A bucket's upper edge on the axis: its inclusive cap plus one price step (77,499.99 → 77,500). */
export function upperOf(c: Contract): number | null {
  if (c.cap == null) return null;
  const places = (c.cap.split('.')[1] ?? '').length;
  return Number(c.cap) + 10 ** -places;
}

/** A 15-minute window is an Up/Down question; underneath it is YES/NO. */
export function sideName(side: 'yes' | 'no', ticker?: string | null) {
  const window = !!ticker && placeOfTicker(ticker)?.horizon === '15m';
  return window ? (side === 'yes' ? 'UP' : 'DOWN') : side.toUpperCase();
}

const DEFAULT_GRID: PriceBand[] = [{ start: '0', end: '1', step: '0.01' }];
/** Every limit the market accepts between 0.1¢ and 99.9¢, in tenths of a cent. */
export function gridPrices(contract: Contract | null | undefined): number[] {
  const out = new Set<number>();
  for (const b of contract?.price_grid?.length ? contract.price_grid : DEFAULT_GRID) {
    const start = Math.round(Number(b.start) * 1000), end = Math.round(Number(b.end) * 1000), step = Math.round(Number(b.step) * 1000);
    if (step <= 0) continue;
    for (let t = start; t <= end; t += step) if (t > 0 && t < 1000) out.add(t);
  }
  return [...out].sort((a, b) => a - b);
}
export function snap(prices: number[], t: number) {
  return prices.reduce((best, p) => Math.abs(p - t) < Math.abs(best - t) ? p : best, prices[0] ?? t);
}
export function stepPrice(prices: number[], t: number, direction: 1 | -1) {
  const i = prices.indexOf(snap(prices, t));
  return prices[Math.min(prices.length - 1, Math.max(0, i + direction))] ?? t;
}
/** The value a strike label shows, without its comparison sign. */
export const level = (c: Contract) => (c.label ?? '').replace(/^[≥>] /, '') || c.strike;
